import { z } from '@/i18n';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, FormError, FormField, FormLabel, FormLabelGroup, Input } from '@opaz/ui-components';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface EmailPasswordInput {
  email: string;
  password: string;
  confirmPassword: string;
}

const emailPasswordFormSchema = z
  .object({
    email: z.string().min(1).email().max(100),
    password: z
      .string()
      .min(12, { message: 'customErrors.passwordMinLength' })
      .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&].*$/, {
        message: 'customErrors.passwordComplexity',
      })
      .max(100),
    confirmPassword: z.string().min(1).max(100),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    params: {
      i18n: {
        key: 'customErrors.passwordMatch',
      },
    },
  });

type EmailPasswordFormProps = {
  data: Omit<EmailPasswordInput, 'confirmPassword'>;
  onComplete: (data: EmailPasswordInput) => void;
};

export const EmailPasswordForm = ({ data: formData, onComplete }: EmailPasswordFormProps) => {
  const { t } = useTranslation();

  const {
    register,
    formState: { errors },
    handleSubmit,
  } = useForm<EmailPasswordInput>({
    values: {
      email: formData.email,
      password: formData.password,
      confirmPassword: formData.password,
    },
    resolver: zodResolver(emailPasswordFormSchema),
  });

  const onSubmit: SubmitHandler<EmailPasswordInput> = async (data) => {
    onComplete(data);
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
      <div className="mb-10">
        <h1 className="font-bold text-3xl text-[#1A444F] mb-6">{t('register.title')}</h1>
        <p className="text-xl text-muted-foreground">{t('register.description')}</p>
      </div>

      <div className="flex flex-col gap-5">
        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.email}>{t('register.fields.email')}</FormLabel>
            {errors.email && <FormError>{t(errors.email.message as unknown as TemplateStringsArray)}</FormError>}
          </FormLabelGroup>

          <Input error={!!errors.email} {...register('email')} autoComplete="off"></Input>
        </FormField>

        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.password}>{t('register.fields.password')}</FormLabel>
            {errors.password && <FormError>{t(errors.password.message as unknown as TemplateStringsArray)}</FormError>}
          </FormLabelGroup>

          <Input
            error={!!errors.password}
            type="password"
            {...register('password')}
            autoComplete="new-password"
          ></Input>
        </FormField>

        <FormField>
          <FormLabelGroup>
            <FormLabel error={!!errors.confirmPassword}>{t('register.fields.confirmPassword')}</FormLabel>
            {errors.confirmPassword && (
              <FormError>{t(errors.confirmPassword.message as unknown as TemplateStringsArray)}</FormError>
            )}
          </FormLabelGroup>

          <Input
            error={!!errors.confirmPassword}
            type="password"
            {...register('confirmPassword')}
            autoComplete="off"
          ></Input>
        </FormField>

        <Button className="mt-10" type="submit">
          {t('register.actionButtonLabel')}
        </Button>
      </div>
    </form>
  );
};
